import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable default-case */
/* eslint-disable consistent-return */
import React from 'react';
import styled, { css } from 'styled-components';
import i18next from 'i18next';
import { Stack } from '@famly/mf_layout_stack';
import { Spinner } from '@famly/mf_feedback_spinner';
import Link from 'web-app/react/components/link/link';
import Add from 'web-app/react/components/icons/legacy/lazy-icons/add';
import Trashcan from 'web-app/react/components/icons/legacy/lazy-icons/trash-can';
import More from 'web-app/react/components/icons/legacy/lazy-icons/more';
import { Text } from '@famly/mf_data-display_text';
import { addHover } from 'web-app/styleguide/utils';
const getSpinnerSize = (spinnerSize, buttonSize) => {
    if (!spinnerSize) {
        return buttonSize === 'mini' ? 15 : 30;
    }
    return spinnerSize;
};
const StyledTrashcan = styled(Trashcan) `
    margin-right: 4px;
`;
const StyledAdd = styled(Add).attrs(props => ({
    fill: props.theme.accent1,
})) `
    margin-right: 6px;
`;
const defaultProps = {
    type: 'button',
    spinner: false,
};
const StyledButtonCSS = css `
    border-radius: 3px;
    margin: 0;
    padding: 4px 16px;
    line-height: 30px;
    text-align: center;
    border: 1px solid ${props => props.theme.delimiter};
    white-space: nowrap;
    box-sizing: border-box;
    cursor: pointer;
    background-color: ${props => props.theme.invertedText};
    color: ${props => props.theme.text};
    outline: none;
    display: inline-block;
    vertical-align: middle;
    font-size: ${props => props.theme.fontConfiguration.sizes.Caption};

    /* State */
    ${addHover(css `
        &:hover:not([disabled]) {
            filter: brightness(97%);
            background-color: ${props => props.theme.backgroundHover};
        }
    `)}

    &:active:not([disabled]) {
        filter: brightness(97%);
    }

    &:focus {
        border: 1px solid ${props => props.theme.textSecondary};
        outline: none;
    }

    &[disabled] {
        opacity: 0.4;
        cursor: default;
    }
`;
const PrimaryButtonCSS = css `
    background-color: ${props => props.theme.primary};
    border-color: ${props => props.theme.primary};
    color: ${props => props.theme.invertedText};
    font-weight: 500;
    &:focus:not([disabled]) {
        background-color: ${props => props.theme.active};
        border-color: ${props => props.theme.primary};
    }
    ${addHover(css `
        &:hover:not([disabled]) {
            background-color: ${props => props.theme.active};
            border-color: ${props => props.theme.active};
        }
    `)}
`;
const StyledButton = styled.div `
    ${props => {
    return (!props.isCustomStyle &&
        css `
                ${StyledButtonCSS}
                ${props.appearance !== 'icon' &&
            css `
                        svg {
                            vertical-align: text-top;
                        }
                    `}
            `);
}}

    /* Appearance specific styling */
    ${props => {
    switch (props.appearance) {
        case 'confirm':
            return PrimaryButtonCSS;
        case 'profile':
            return css `
                    box-shadow:
                        0 0 1px rgba(0, 0, 0, 0.11),
                        0 1px 2px rgba(0, 0, 0, 0.11);
                    border: 0;
                    border-right: 1px solid ${props => props.theme.delimiter};

                    &:focus:not([disabled]) {
                        border: inherit;
                        border-right: 1px solid ${props => props.theme.delimiter};
                    }
                `;
        case 'icon':
            return css `
                    background-color: transparent;
                    border: 1px solid transparent;
                    ${addHover(css `
                        &:hover:not([disabled]) {
                            background-color: transparent;
                            border-color: transparent;
                            filter: brightness(97%);
                        }
                    `)}
                    &:focus:not([disabled]) {
                        background-color: transparent;
                        border-color: transparent;
                        filter: brightness(97%);
                    }
                    && {
                        line-height: 0;
                    }

                    ${props.buttonSize === 'mini' &&
                css `
                            padding: 8px;
                        `}
                `;
        case 'more':
            return css `
                    &,
                    &:focus,
                    &:active,
                    &:hover,
                    &&:focus:not(:active) {
                        border-color: transparent;
                    }
                    ${props.buttonSize === 'mini' &&
                css `
                            padding: 8px;
                        `}
                `;
        case 'link':
        case 'silent':
            return css `
                    text-decoration: none;
                    color: ${() => {
                switch (props.appearance) {
                    case 'silent':
                        return props.theme.textDisabled;
                    default:
                        return props.theme.analogue2;
                }
            }};
                    padding: 0;
                    line-height: normal;
                    border: none;
                    background-color: transparent;
                    ${props.appearance === 'silent'
                ? css `
                                  text-decoration: underline;
                              `
                : ''}

                    &,
                    &:focus,
                    &:active,
                    &:hover,
                    &:hover:not([disabled]),
                    &:focus:not(:active) {
                        background-color: transparent;
                        border-color: transparent;
                        border: none;
                    }
                `;
        case 'secondary':
            return css `
                    background: ${props => props.theme.background};
                    border: 1px solid ${props => props.theme.delimiter};
                    padding: 2px 16px;
                    color: ${props => props.theme.accent1};
                    font-weight: 600;
                `;
        case 'textSecondary':
            return css `
                    color: ${props => props.theme.textSecondary};
                `;
        case 'add':
            return css `
                    &,
                    &:focus,
                    &:active,
                    &:hover,
                    &:focus:not(:active) {
                        border-color: transparent;
                    }
                `;
        default:
            return '';
    }
}}

    /* Styling excluded by appearance */
    ${props => props.appearance === 'none' || props.appearance === 'asDefault'
    ? ''
    : css `
                  &[type='submit'] {
                      ${PrimaryButtonCSS}
                  }
              `}
    ${props => props.appearance === 'none' || props.appearance === 'icon'
    ? ''
    : css `
                  &:focus:not(:active) {
                      border-color: ${props => props.theme.borderConfiguration.focus};
                  }
              `}

    /* Opacity */
    ${props => {
    switch (props.opacity) {
        case 'transparent':
            return css `
                    background-color: ${props => props.theme.invertedText};
                    border: 1px solid ${props => props.theme.delimiter};
                    color: ${props => props.theme.textSecondary};
                `;
        case 'fade':
            return css `
                    opacity: 0.6;
                `;
    }
}}

    /* Size */
    ${props => {
    switch (props.buttonSize) {
        case 'mini':
            return css `
                    padding: 6px 10px;
                    line-height: 20px;
                    border-radius: 4px;
                `;
        case 'small':
            return css `
                    padding: 10px 10px;
                    line-height: 20px;
                    border-radius: 4px;
                `;
        case 'medium':
            return css `
                    padding: 8px 16px;
                    line-height: 21px;
                `;
        case 'big':
            return css `
                    padding: 20px 25px;
                    line-height: 25px;
                `;
        case 'auto':
            return css `
                    width: auto;
                `;
    }
}}

    /* Position */
    ${props => {
    switch (props.position) {
        case 'left':
            return css `
                    float: left;
                    width: auto;
                `;
        case 'right':
            return css `
                    float: right;
                    width: auto;

                    & + button,
                    & + a {
                        ${props.position === 'right' &&
                css `
                                margin-left: 0;
                                margin-right: 8px;
                            `}
                    }
                `;
        case 'fill':
            return css `
                    width: 100%;
                `;
    }
}}

    /* Box */
    ${props => props.box === 'inline' &&
    css `
            display: inline-block;
            width: auto;

            & + button,
            & + a {
                margin-left: 8px;
            }
        `}

    ${props => props.isNoneStyle &&
    css `
            padding: 0;
            border: none;
            line-height: inherit;
            border-radius: 0;
            text-align: inherit;
            background: none;

            &:focus {
                border: none;
                outline: none;
            }
        `}

    /* Align icons */
    > img {
        vertical-align: middle;
    }
`;
const WarningImage = styled.img `
    width: 19px;
    height: 15px;
    margin-right: 4px;
    margin-top: -2px;
`;
class ButtonClass extends React.Component {
    constructor(props) {
        super(props);
        this._isMounted = true;
        this.state = {
            promiseLoading: false,
        };
        this.handleClick = this.handleClick.bind(this);
    }
    render() {
        const { children, className, disabled, isDisabled, onMouseDown, onMouseUp, onMouseEnter, onMouseLeave, appearance, buttonSize, position, opacity, spinner, spinnerSize, box, type, showAlert, spinnerId, href, ...directProps } = this.props;
        // We don't want onClick or promiseOnClick to be passed to the button element
        const { onClick, promiseOnClick, ...filteredDirectProps } = directProps;
        const { promiseLoading } = this.state;
        const isNoneStyle = appearance === 'none';
        const isCustomStyle = appearance === 'custom' || isNoneStyle;
        const attributes = {
            className,
            onClick: this.handleClick,
            onMouseDown,
            onMouseUp,
            onMouseEnter,
            onMouseLeave,
            disabled: '',
        };
        // If spinner is true, we disable the button unless isDisabled or disabled is explicitly set to false
        const shouldDisableButton = Boolean(disabled || isDisabled || promiseLoading || (spinner && !(isDisabled === false || disabled === false)));
        if (shouldDisableButton) {
            attributes.disabled = 'disabled';
        }
        let childElement = isCustomStyle || !showAlert ? (children) : (_jsxs("div", { children: [showAlert ? (_jsx(WarningImage, { src: "img/icons/warning-sign.png", alt: i18next.t($ => $.notices.warning) })) : null, children] }));
        if (appearance === 'delete') {
            childElement = (_jsxs(Stack, { alignItems: "center", children: [_jsx(StyledTrashcan, {}), _jsx("span", { children: children ? children : i18next.t($ => $.delete) })] }));
        }
        if (appearance === 'add') {
            childElement = (_jsxs("div", { children: [_jsx(StyledAdd, { size: 20 }), _jsx(Text, { variant: "body", component: "span", display: "inline", children: children ? children : i18next.t($ => $.add) })] }));
        }
        if (appearance === 'deleteNoText') {
            childElement = (_jsx("div", { children: _jsx(Trashcan, {}) }));
        }
        if (appearance === 'more') {
            childElement = _jsx(More, { size: 20 });
        }
        if (appearance === 'x') {
            childElement = _jsx("span", { children: "\u00D7" });
        }
        if (spinner || promiseLoading) {
            childElement = (_jsx(Stack, { children: _jsx(Spinner, { "data-e2e-id": spinnerId, size: getSpinnerSize(spinnerSize, buttonSize), "aria-label": i18next.t($ => $.react.components.loading.spinner.translations.json.loadingAriaLabel) }) }));
        }
        if (type === 'link') {
            return (_jsx(StyledButton, { as: Link, ...attributes, ...filteredDirectProps, href: shouldDisableButton ? undefined : href, isCustomStyle: isCustomStyle, disabled: shouldDisableButton, isNoneStyle: isNoneStyle, buttonSize: buttonSize, position: position, box: box, opacity: opacity, appearance: appearance, children: childElement }));
        }
        return (_jsx(StyledButton, { as: "button", type: type, role: "button", ...attributes, ...filteredDirectProps, disabled: shouldDisableButton, isCustomStyle: isCustomStyle, isNoneStyle: isNoneStyle, buttonSize: buttonSize, position: position, box: box, opacity: opacity, appearance: appearance, children: childElement }));
    }
    componentWillUnmount() {
        // We do this to make sure we don't update state on an unmounted button if promiseOnClick is set
        this._isMounted = false;
    }
    handleClick(...args) {
        if (!this.props.onClick) {
            return;
        }
        const onClickReturnValue = this.props.onClick(...args);
        if (this.props.promiseOnClick && onClickReturnValue && typeof onClickReturnValue.finally === 'function') {
            this.setState({
                promiseLoading: true,
            });
            onClickReturnValue.finally(() => {
                if (this._isMounted) {
                    this.setState({
                        promiseLoading: false,
                    });
                }
            });
        }
    }
}
ButtonClass.defaultProps = defaultProps;
/**
 * Export button as styled component
 *
 * @deprecated Prefer using the button component from Modern Famly
 */
export default styled(ButtonClass) ``;
